// @file Composable for whiteboard page handlers
import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import type { PageMeta } from '@@/bits/whiteboard'
import { findNearestFrame } from '@@/bits/whiteboard'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { cloneDeep } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import type { Editor, TLPage, TLPageId } from 'tldraw'

const useWhiteboardPageHandlers = (
  editor: Editor,
): {
  updatePageAttribution: (page: TLPage) => TLPage
  registerSideEffects: () => void
} => {
  const { currentUser } = storeToRefs(useSurfaceCurrentUserStore())
  const whiteboardStore = useWhiteboardStore()
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const whiteboardModeStore = useWhiteboardModeStore()

  // #region Helpers
  const isPageAttributionPopulated = (meta: TLPage['meta']): boolean => {
    return meta.lastModifiedBy != null && meta.lastModifiedAt != null
  }
  // #endregion

  // #region PAGE (frame) SIDE EFFECTS
  const updatePageAttribution = (page: TLPage): TLPage => {
    const clonedPage = cloneDeep(page)
    if (page === undefined || clonedPage === undefined) {
      captureException(new Error('Page is undefined on before change'), { tags: { isTldrawError: true } })
      return page
    }
    clonedPage.meta = clonedPage.meta ?? {}
    clonedPage.meta.lastModifiedAt = Date.now()
    clonedPage.meta.lastModifiedBy = currentUser.value.id
    return clonedPage
  }

  const registerSideEffects = (): void => {
    editor.sideEffects.registerBeforeCreateHandler('page', (page, source) => {
      if (source !== 'user') return page

      // Assign attribution when page is created. Page attribution can already be populated
      // if the page is duplicated (in which case we keep its original meta).
      if (!isPageAttributionPopulated(page.meta)) {
        const clonedPage = cloneDeep(page)
        clonedPage.meta = {
          // Page meta can have other attributies like `backgroundType`. We need to keep them.
          ...clonedPage.meta,
          lastModifiedAt: Date.now(),
          lastModifiedBy: currentUser.value.id,
          useThemeFrameBackground: isAppUsing('sandboxThemeBackgrounds'),
        }
        return clonedPage
      }

      return page
    })

    editor.sideEffects.registerBeforeChangeHandler('page', (_prev, next, source) => {
      if (source !== 'user') return next
      return updatePageAttribution(next)
    })

    // When a page becomes admin-only, move the current user off of it if they can no
    // longer see it (non-admin, or admin in play mode). Uses the sideEffects hook rather
    // than a reactive effect so (a) it covers remote changes from collaborators' toggles
    // via sync, and (b) it doesn't fire on initial mount — sandbox exports relied on
    // the initial-fire of a reactive fallback, which raced with URL-driven navigation
    // and caused every screenshot to capture the same frame.
    editor.sideEffects.registerAfterChangeHandler('page', (prev, next) => {
      const wasAdminOnly = (prev.meta as PageMeta)?.isAdminOnly === true
      const isAdminOnly = (next.meta as PageMeta)?.isAdminOnly === true
      if (wasAdminOnly || !isAdminOnly) return
      if (editor.getCurrentPageId() !== next.id) return

      const userCanSeeAdminOnly = whiteboardPermissionsStore.canIAdminister && !whiteboardModeStore.isPlayMode
      if (userCanSeeAdminOnly) return

      // Exclude next.id from the frame list: whiteboardStore.frames is updated by a
      // reactive effect that runs in the microtask after this synchronous afterChange
      // handler, so at this point it may still list the just-toggled page as visible.
      const availableFrames = whiteboardStore.frames.filter((f) => f.id !== next.id)
      const nearest = findNearestFrame(editor.getPages(), availableFrames, next.id)
      if (nearest != null) {
        editor.setCurrentPage(nearest.id as TLPageId)
      }
    })
  }
  // #endregion

  return { updatePageAttribution, registerSideEffects }
}
export { useWhiteboardPageHandlers }
